import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    Button,
    Box,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    alpha,
    useTheme
} from '@mui/material';
import {
    School,
    Save,
    Cancel,
    CloudUpload
} from '@mui/icons-material';
import { uploadTipImage } from '../../api/tips';

const TipEditDialog = ({
    open,
    onClose,
    tip,
    categories,
    onSave,
    loading = false
}) => {
    const theme = useTheme();
    const [formData, setFormData] = useState({
        title: '',
        tipText: '',
        categoryId: '',
        imageUrl: '',
        isActive: true
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (tip) {
            setFormData({
                title: tip.title || '',
                tipText: tip.tipText || '',
                categoryId: tip.category?.id || '',
                imageUrl: tip.imageUrl || '',
                isActive: tip.isActive !== undefined ? tip.isActive : true
            });
            setImagePreview(tip.imageUrl || '');
        } else {
            setFormData({
                title: '',
                tipText: '',
                categoryId: '',
                imageUrl: '',
                isActive: true
            });
            setImagePreview('');
        }
        setSelectedImage(null);
        setError('');
    }, [tip, open]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                setError('Please select a valid image file (PNG, JPG, JPEG, WEBP, or GIF)');
                return;
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                setError('Image file size should be less than 5MB');
                return;
            }

            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
            setError('');
        }
    };

    const handleSubmit = async () => {
        if (!formData.tipText.trim()) {
            setError('Tip text is required');
            return;
        }

        setUploading(true);
        setError('');

        try {
            let finalImageUrl = formData.imageUrl;

            // If a new image is selected, upload it first
            if (selectedImage) {
                const imageResponse = await uploadTipImage(selectedImage);
                finalImageUrl = imageResponse.data;
            }

            const tipData = {
                title: formData.title.trim() || null,
                tipText: formData.tipText.trim(),
                categoryId: formData.categoryId || null,
                imageUrl: finalImageUrl || null,
                isActive: formData.isActive
            };

            await onSave(tipData);

        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save tip');
            console.error('Error saving tip:', err);
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            title: '',
            tipText: '',
            categoryId: '',
            imageUrl: '',
            isActive: true
        });
        setSelectedImage(null);
        setImagePreview('');
        setError('');
        onClose();
    };

    const getCategoryColor = (categoryName) => {
        const colors = {
            'Compost': '#4caf50',
            'Recycle': '#2196f3',
            'Hazardous': '#f44336',
            'General_Waste': '#9e9e9e',
            'E-Waste': '#ff9800'
        };
        return colors[categoryName] || theme.palette.primary.main;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, maxHeight: '90vh' }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
                color: 'white',
                pb: 2
            }}>
                <Box sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <School />
                    {tip ? 'Edit Educational Tip' : 'Create New Educational Tip'}
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pb: 2, pt: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Title */}
                    <TextField
                        fullWidth
                        label="Title (Optional)"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder="Enter a catchy title for your tip"
                        helperText="A compelling title helps users understand the tip quickly"
                    />

                    {/* Tip Content */}
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Tip Content *"
                        value={formData.tipText}
                        onChange={(e) => handleInputChange('tipText', e.target.value)}
                        placeholder="Write your educational tip here..."
                        required
                        helperText={`${formData.tipText.length}/1000 characters`}
                        inputProps={{ maxLength: 1000 }}
                    />

                    {/* Category */}
                    <FormControl fullWidth>
                        <InputLabel>Category (Optional)</InputLabel>
                        <Select
                            value={formData.categoryId}
                            onChange={(e) => handleInputChange('categoryId', e.target.value)}
                            label="Category (Optional)"
                        >
                            <MenuItem value="">
                                <em>No category (General tip)</em>
                            </MenuItem>
                            {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: getCategoryColor(category.name)
                                            }}
                                        />
                                        {category.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Image Upload */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                            📷 Image (Optional)
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUpload />}
                                sx={{ flexShrink: 0 }}
                            >
                                Choose Image
                                <input
                                    type="file"
                                    hidden
                                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                                    onChange={handleImageSelect}
                                />
                            </Button>

                            {imagePreview && (
                                <Box sx={{ position: 'relative' }}>
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{
                                            width: 120,
                                            height: 80,
                                            objectFit: 'cover',
                                            borderRadius: 8,
                                            border: `2px solid ${theme.palette.divider}`
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setImagePreview('');
                                            setSelectedImage(null);
                                            handleInputChange('imageUrl', '');
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            background: theme.palette.error.main,
                                            color: 'white',
                                            '&:hover': { background: theme.palette.error.dark },
                                            width: 24,
                                            height: 24
                                        }}
                                    >
                                        <Cancel fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Supported formats: PNG, JPG, JPEG, WEBP, GIF (Max 5MB)
                        </Typography>
                    </Box>

                    {/* Active Switch */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                            />
                        }
                        label="Active (visible to users)"
                    />
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleClose} disabled={uploading || loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={uploading || loading || !formData.tipText.trim()}
                    startIcon={uploading || loading ? <CircularProgress size={16} /> : <Save />}
                    sx={{
                        background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #2e7d32, #43a047)'
                        }
                    }}
                >
                    {uploading || loading ? 'Saving...' : (tip ? 'Update Tip' : 'Create Tip')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TipEditDialog;
